import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import ExposureResolve from '@/components/base/ExposureResolve';
import FinalCta from '@/components/feature/FinalCta';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import PreviewNotice from '@/components/base/PreviewNotice';
import NotFound from '@/pages/NotFound';
import type { Technician } from '@/types/customer';
import { technicians } from '@/mocks/technicians';
import { services } from '@/mocks/services';

export default function TechnicianDetailPage() {
  const { slug } = useParams();
  const tech = (technicians as Technician[]).find((t) => t.slug === slug);
  if (!tech) return <NotFound />;
  const related = services.filter((s) => tech.serviceSlugs.includes(s.slug));

  return (
    <PageShell>
      <section className="bg-background-50 pt-10 md:pt-14 pb-16 md:pb-24">
        <Container>
          <nav aria-label="Breadcrumb" className="flex flex-wrap gap-2 text-xs text-foreground-600">
            <Link to="/" className="mw-link hover:text-foreground-950">Home</Link><span>/</span>
            <Link to="/technicians" className="mw-link hover:text-foreground-950">Technicians</Link><span>/</span>
            <span className="text-foreground-950">{tech.name}</span>
          </nav>
          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <ExposureResolve key={tech.slug} className="h-[460px] md:h-[600px] w-full overflow-hidden rounded-xl bg-primary-50">
                <img src={tech.portrait} alt={`${tech.name}, ${tech.role}`} title={`${tech.name} - MENDWELL technician`} className="h-full w-full object-cover object-top" />
              </ExposureResolve>
            </div>
            <div className="lg:col-span-7">
              <Eyebrow label={tech.role} />
              <h1 className="mt-6 text-[38px] md:text-[56px] font-medium leading-[1.02] tracking-[-0.03em] text-foreground-950">{tech.name}</h1>
              <p className="mt-6 max-w-xl text-[15px] md:text-base leading-relaxed text-foreground-600">{tech.intro}</p>
              {tech.isSample && <div className="mt-6 max-w-xl"><PreviewNotice>Sample profile for visual demonstration. Not a real MENDWELL technician.</PreviewNotice></div>}

              <div className="mt-10 border-t border-background-200 pt-8">
                <h2 className="text-[11px] uppercase tracking-[0.16em] text-foreground-600">Appliance specializations</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {related.map((s) => <span key={s.slug} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{s.short}</span>)}
                </div>
              </div>
              <div className="mt-8 border-t border-background-200 pt-8">
                <h2 className="text-[11px] uppercase tracking-[0.16em] text-foreground-600">Types of repair work</h2>
                <ul className="mt-4">
                  {tech.repairTypes.map((r) => (
                    <li key={r} className="flex items-center gap-3 border-b border-background-200 py-3 text-[15px] text-foreground-950">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />{r}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-8">
                <h2 className="text-[11px] uppercase tracking-[0.16em] text-foreground-600">Service philosophy</h2>
                <p className="mt-4 max-w-xl text-xl md:text-2xl font-light leading-snug tracking-tight text-foreground-950">&ldquo;{tech.philosophy}&rdquo;</p>
              </div>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to="/book" className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm text-white hover:bg-primary-600 mw-btn">Book a Repair</Link>
                <p className="text-xs text-foreground-600">Booking does not guarantee a specific technician.</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <Container>
          <Eyebrow label="Related Services" />
          <div className="mt-8">
            {related.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center justify-between gap-4 border-t border-background-200 py-6 last:border-b">
                <span className="text-lg md:text-2xl font-light tracking-tight text-foreground-950 group-hover:text-primary-600 transition-colors">{s.name}</span>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-950"><i className="ri-arrow-right-up-line" /></span>
              </Link>
            ))}
          </div>
        </Container>
      </section>
      <FinalCta />
    </PageShell>
  );
}